<template>
  <div>
    <h2 class="text-left alert" :class="[ removed ? 'alert-danger' : 'alert-success' ]">
      Mastitis
      <a class="btn btn-sm" @click="$emit('onRemoveSection', stepName)">
        <span class="glyphicon" :class="[ removed ? 'glyphicon-plus' : 'glyphicon-remove' ]"></span>
      </a>
    </h2>

    <template v-if="!removed">
      <div class="text-left step-section">
        <h3 :class="isRemovedSubsection('dry_cow_used') ? 'subsection-disabled' : ''">
          <u>Dry Cow Used</u>
          <a class="btn btn-sm" @click="$emit('onRemoveSection', stepName, 'dry_cow_used')">
            <span class="glyphicon" :class="isRemovedSubsection('dry_cow_used') ? 'glyphicon-plus' : 'glyphicon-remove'">
            </span>
          </a>
        </h3>
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>Product</th>
              <th>{{ seasonStartYear - 1 }} / {{ seasonStartYear }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(rv, index) in model.CHARTS.drycow_sales" :key="index">
              <td class="text-left">{{ rv["Product"] }}</td>
              <td class="text-left">{{ rv["qty"] }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="text-left step-section">
        <h3 :class="isRemovedSubsection('cows_treated_spring_mastitis') ? 'subsection-disabled' : ''">
          <u>Cows Treated Spring Mastitis</u>
          <a class="btn btn-sm" @click="$emit('onRemoveSection', stepName, 'cows_treated_spring_mastitis')">
            <span class="glyphicon" :class="isRemovedSubsection('cows_treated_spring_mastitis') ? 'glyphicon-plus' : 'glyphicon-remove'">
            </span>
          </a>
        </h3>
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>Season</th>
              <th>Total Cows Wintered</th>
              <th>Cows Treated</th>
              <th>Percent Cows Wintered</th>
              <th>Percent Region Avg</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(rv, index) in model.CHARTS.spring_mastitis" :key="index"
              :class="getRowClass(model.CHARTS.spring_mastitis, 'Season', index)">

              <td class="text-left">{{ rv["Season"] }}</td>
              <td class="text-left">{{ rv["Total Cows Wintered"] }}</td>
              <td class="text-left">{{ rv["Cows Treated"] }}</td>
              <td class="text-left">{{ rv["Percent Total Cows Wintered"] }}</td>
              <td class="text-left">{{ rv["Region Avg. Percent Total Cows Wintered"] }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="text-left step-section">
        <h3 :class="isRemovedSubsection('calving_mastitis') ? 'subsection-disabled' : ''">
          <u>Calving Mastitis</u>
          <a class="btn btn-sm" @click="$emit('onRemoveSection', stepName, 'calving_mastitis')">
            <span class="glyphicon" :class="isRemovedSubsection('calving_mastitis') ? 'glyphicon-plus' : 'glyphicon-remove'">
            </span>
          </a>
        </h3>

        <div class="text-left">
          <dl class="dl-horizontal">
            <dt>Of the {{ totalCowsTreated }} cows treated, what percentage were heifers?</dt>
            <dd>{{ model.est_percent_mastitis_treat_is_heifers || '-'}}</dd>

            <dt>Estimated % of treatments that are mixed age</dt>
            <dd>{{ estPercentMastitisTreatMixed || '-'}}</dd>
          </dl>
        </div>

        <table class="table table-bordered" v-if="hasSpringMastitisBreakdownData">
          <thead>
            <tr>
              <th>Group</th>
              <th>Animal numbers</th>
              <th>Treated</th>
              <th>Percent of group treated</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="text-left">Total Cows Wintered</td>
              <td class="text-left">{{ model.CHARTS.spring_mastitis_breakdown[0]["cows_wintered_incl_r2"] }}</td>
              <td class="text-left">{{ model.CHARTS.spring_mastitis_breakdown[0]["cows_treated"] }}</td>
              <td class="text-left">{{ model.CHARTS.spring_mastitis_breakdown[0]["percent_treated"] }}</td>
            </tr>
            <tr>
              <td class="text-left">Mixed Age</td>
              <td class="text-left">{{ model.CHARTS.spring_mastitis_breakdown[0]["mixed_age"] }}</td>
              <td class="text-left">{{ mixedAgeTreated }}</td>
              <td class="text-left">{{ mixedAgePercentTreated }}</td>
            </tr>
            <tr>
              <td class="text-left">Heifers</td>
              <td class="text-left">{{ model.CHARTS.spring_mastitis_breakdown[0]["heifers"] }}</td>
              <td class="text-left"> {{ heifersTreated }} </td>
              <td class="text-left"> {{ heifersPercentTreated }} </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="text-left step-section">
        <h3 :class="isRemovedSubsection('mastitis_treatments') ? 'subsection-disabled' : ''">
          <u>Mastitis Treatments</u>
          <a class="btn btn-sm" @click="$emit('onRemoveSection', stepName, 'mastitis_treatments')">
            <span class="glyphicon" :class="isRemovedSubsection('mastitis_treatments') ? 'glyphicon-plus' : 'glyphicon-remove'">
            </span>
          </a>
        </h3>
        <bar :chart-data="treatmentsChartData" :options="chartOptions" ref="treatments_chart"></bar>

        <table class="table table&#45;bordered">
          <thead>
            <tr>
              <th></th>
              <th v-for="(season, index) in chartMastitisTreatmentsDates" :key="index"> {{ season }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(product, productIndex) in chartMastitisTreatmentsProducts" :key="productIndex">
              <td>{{ product }}</td>
              <td v-for="(date, dateIndex) in chartMastitisTreatmentsDates" :key="dateIndex">
                {{ getProductValueForDate(product, date) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="text-left step-section">
        <dl class="dl-horizontal">
          <dt>Key Points</dt>
          <dd>{{ model.dcat_keypoints || '-'}}</dd>

          <dt>Actions</dt>
          <dd>{{ model.dcat_actions || '-'}}</dd>
        </dl>
      </div>
    </template>
  </div>
</template>

<script>
import mixin from './mixin'
import Bar from '../../charts/Bar'

export default {
  mixins: [ mixin ],
  props: ['model'],
  components: {
    Bar,
  },
  data() {
    return {
      stepName: 'Mastitis',
    }
  },
  methods: {
    getProductValueForDate: function(product, season) {
      var value = this.model.CHARTS.spring_mastitis_treatments.filter(x => x['Season'] == season && x['Product'] == product)
      if (value.length > 0) {
        return value[0]['Cows Treated']
      }
      return 0
    },
  },
  computed: {
    hasSpringMastitisBreakdownData: function ()
    {
      return (this.model.CHARTS.spring_mastitis_breakdown || []).length > 0
    },
    totalCowsTreated: function ()
    {
      return (this.hasSpringMastitisBreakdownData)
        ? this.model.CHARTS.spring_mastitis_breakdown[0]["cows_treated"]
        : 0
    },
    estPercentMastitisTreatMixed: function() {
      if (this.model.est_percent_mastitis_treat_is_heifers) {
        return 100 - this.model.est_percent_mastitis_treat_is_heifers
      }
      return 0
    },
    mixedAgeTreated: function() {
      return Math.round(this.estPercentMastitisTreatMixed / 100 * this.totalCowsTreated, 1)
    },
    mixedAgePercentTreated: function() {
      let mixedAge

      if (this.model.CHARTS.spring_mastitis_breakdown[0]){
        mixedAge = this.model.CHARTS.spring_mastitis_breakdown[0]['mixed_age']
      } else {
        mixedAge = 0
      }

      return Math.round(this.mixedAgeTreated / mixedAge * 100, 1)
    },
    heifersTreated: function() {
      return Math.round(this.model.est_percent_mastitis_treat_is_heifers / 100 * this.totalCowsTreated, 1)
    },
    heifersPercentTreated: function() {
      let heifers = this.model.CHARTS.spring_mastitis_breakdown[0]["heifers"]

      return Math.round(this.heifersTreated / heifers * 100, 1)
    },
    chartOptions: function () {
      return {
        responsive: true,
        scales: {
          xAxes: [{
            stacked: true,
            categoryPercentage: 0.5,
            barPercentage: 1
          }],
          yAxes: [{
            stacked: true,
            scaleLabel: {
              display: true,
              labelString: 'Cows Treated'
            }
          }]
        }
      }
    },
    chartMastitisTreatmentsDates: function() {
      return Array.from(new Set(this.model.CHARTS.spring_mastitis_treatments.map(x => x['Season']))).reverse()
    },
    chartMastitisTreatmentsProducts: function() {
      return Array.from(new Set(this.model.CHARTS.spring_mastitis_treatments.map(x => x['Product'])))
    },
    treatmentsChartData: function() {
      var self = this;

      var seasons  = this.chartMastitisTreatmentsDates
      var products = this.chartMastitisTreatmentsProducts

      var datasets = []

      products.forEach(function(productName) {
        var product = {
          label: productName,
          backgroundColor: '#'+(Math.random()*0xFFFFFF<<0).toString(16),
          data: []
        }
        seasons.forEach(function(seasonName){
          product['data'].push(self.getProductValueForDate(productName, seasonName))
        })

        datasets.push(product)
      })

      return {
        labels: seasons,
        datasets: datasets
      }
    },
  },
};
</script>
